/* NAVEALO commerce behaviour: offer cards, extras and multi-product add. Prices come from Liquid data. */
import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

const money = (cents) => formatMoney(cents, window.NV?.moneyFormat || '{{amount}}', window.NV?.currency || 'USD');

async function fetchCart() {
  const response = await fetch(`${Theme.routes.cart_url}.json`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
  return response.json();
}

/**
 * Adds several lines in one request and keeps Horizon's cart drawer / cart count in sync
 * by using the same CartLinesUpdateEvent flow as the native add-to-cart button.
 */
async function addItems(target, items) {
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => el.dataset.sectionId)
    .filter(Boolean)
    .join(',');
  const total = items.reduce((sum, item) => sum + item.quantity, 0);
  const deferred = CartLinesUpdateEvent.createPromise();

  target.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: items.map((item) => ({ id: Number(item.id), quantity: item.quantity })),
        sections: sectionIds,
      }),
    });
    const data = await response.json();
    const cart = await fetchCart();

    if (data.status) {
      const message = data.description || data.message || 'Add to cart failed';
      target.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID', detail: { description: data.description } }));
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { didError: true, items: cart.items, source: 'navealo', sourceId: target.id, itemCount: total },
      });
      return { ok: false, message };
    }

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'navealo',
        sourceId: target.id,
        itemCount: total,
        sections: data.sections,
        didError: false,
      },
    });
    return { ok: true };
  } catch (error) {
    deferred.reject(error);
    target.dispatchEvent(new CartErrorEvent({ error: error?.message || 'Network error', code: 'SERVICE_UNAVAILABLE' }));
    return { ok: false, message: error?.message || 'Network error' };
  }
}

/* Offer cards ------------------------------------------------------------------ */
class NVOffers extends HTMLElement {
  #variants = new Map();
  #variant = null;
  #section = null;

  connectedCallback() {
    const data = this.querySelector('script[data-nv-variants]');
    try {
      JSON.parse(data.textContent).forEach((v) => this.#variants.set(String(v.id), v));
    } catch (error) {
      /* no variant data: keep server-rendered prices */
    }
    this.#variant = this.#variants.get(this.dataset.variant) || [...this.#variants.values()][0] || null;
    /* Always have one offer selected: fall back to the first available card */
    if (!this.selected) {
      const first = this.querySelector('.nv-offer__input:not(:disabled)');
      if (first) first.checked = true;
    }
    this.addEventListener('change', this.#onChange);
    document.addEventListener('submit', this.#onSubmit, true);
    this.#section = this.closest('.shopify-section');
    this.#section?.addEventListener(StandardEvents.productSelect, this.#onSelect);
    this.render();
  }

  disconnectedCallback() {
    document.removeEventListener('submit', this.#onSubmit, true);
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#onSelect);
  }

  get offers() {
    return [...this.querySelectorAll('.nv-offer')];
  }

  get selected() {
    return this.querySelector('.nv-offer__input:checked')?.closest('.nv-offer') ?? null;
  }

  #onChange = (event) => {
    if (event.target.matches?.('.nv-offer__input')) this.render();
  };

  #onSelect = async (event) => {
    try {
      const { detail } = await event.promise;
      const id = detail?.resource?.id;
      const variant = id ? this.#variants.get(String(id)) : null;
      if (variant) {
        this.#variant = variant;
        this.render();
      }
    } catch (error) {
      /* aborted variant change */
    }
  };

  render() {
    const v = this.#variant;
    for (const el of this.offers) {
      const input = el.querySelector('.nv-offer__input');
      el.classList.toggle('is-selected', input.checked);
      if (!v) continue;
      const qty = Number(el.dataset.qty) || 1;
      const pct = Number(el.dataset.pct) || 0;
      const extraQty = Number(el.dataset.extraQty) || 0;
      const extraPrice = Number(el.dataset.extraPrice) || 0;
      const base = v.price * qty + extraPrice * extraQty;
      const compareUnit = v.compare_at_price && v.compare_at_price > v.price ? v.compare_at_price : v.price;
      const compareBase = compareUnit * qty + extraPrice * extraQty;
      const now = Math.round((base * (100 - pct)) / 100);
      const was = compareBase > now ? compareBase : 0;
      const nowEl = el.querySelector('[data-offer-now]');
      const wasEl = el.querySelector('[data-offer-was]');
      const perEl = el.querySelector('[data-offer-per]');
      if (nowEl) nowEl.textContent = money(now);
      if (wasEl) {
        wasEl.textContent = was ? money(was) : '';
        wasEl.hidden = !was;
      }
      if (perEl && qty > 1) perEl.textContent = money(Math.round(now / qty));
    }
  }

  #message(text) {
    const region = this.querySelector('[data-offers-message]');
    if (!region) return;
    region.textContent = text || '';
    region.hidden = !text;
  }

  #onSubmit = async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const scope = this.closest('product-component');
    if (!scope || !scope.contains(form)) return;
    const offer = this.selected;
    if (!offer) return;
    const qty = Number(offer.dataset.qty) || 1;
    const extraId = offer.dataset.extraVariant;
    if (qty === 1 && !extraId) return; // native add-to-cart handles a single unit

    event.preventDefault();
    event.stopImmediatePropagation();

    const variantId = form.querySelector('input[name="id"]')?.value;
    if (!variantId) return;
    const items = [{ id: variantId, quantity: qty }];
    if (extraId) items.push({ id: extraId, quantity: Number(offer.dataset.extraQty) || 1 });

    const button = form.querySelector('[ref="addToCartButton"]');
    if (button?.disabled) return;
    button?.setAttribute('aria-busy', 'true');
    if (button) button.disabled = true;
    this.#message('');

    const result = await addItems(scope, items);

    if (button) {
      button.disabled = false;
      button.removeAttribute('aria-busy');
      if (result.ok) {
        button.setAttribute('data-added', 'true');
        setTimeout(() => button.removeAttribute('data-added'), 2000);
      }
    }
    if (!result.ok) this.#message(result.message);
  };
}

/* Bundle: add several products in one click -------------------------------------- */
class NVBundle extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-bundle-add]');
    this.addEventListener('change', () => this.render());
    this.button?.addEventListener('click', () => this.add());
    this.render();
  }

  get lines() {
    return [...this.querySelectorAll('.nv-bundle__item')]
      .filter((item) => item.querySelector('[data-bundle-check]')?.checked)
      .map((item) => {
        const control = item.querySelector('[data-bundle-variant]');
        const option = control.tagName === 'SELECT' ? control.selectedOptions[0] : control;
        return {
          el: item,
          id: option.value,
          price: Number(option.dataset.price) || 0,
          compare: Number(option.dataset.compare) || 0,
          available: option.dataset.available === 'true',
        };
      });
  }

  render() {
    const lines = this.lines;
    const total = lines.reduce((sum, line) => sum + line.price, 0);
    const compare = lines.reduce((sum, line) => sum + (line.compare > line.price ? line.compare : line.price), 0);
    const pct = Number(this.dataset.discount) || 0;
    const now = Math.round((total * (100 - pct)) / 100);

    this.querySelectorAll('.nv-bundle__item').forEach((item) => {
      const control = item.querySelector('[data-bundle-variant]');
      const option = control.tagName === 'SELECT' ? control.selectedOptions[0] : control;
      const priceEl = item.querySelector('[data-bundle-price]');
      if (priceEl) priceEl.textContent = money(Number(option.dataset.price) || 0);
      item.classList.toggle('is-selected', item.querySelector('[data-bundle-check]').checked);
      item.classList.toggle('is-unavailable', option.dataset.available !== 'true');
    });

    const totalEl = this.querySelector('[data-bundle-total]');
    const wasEl = this.querySelector('[data-bundle-was]');
    if (totalEl) totalEl.textContent = money(now);
    if (wasEl) {
      const was = compare > now ? compare : 0;
      wasEl.textContent = was ? money(was) : '';
      wasEl.hidden = !was;
    }
    const countEl = this.querySelector('[data-bundle-count]');
    if (countEl) countEl.textContent = String(lines.length);
    if (this.button) this.button.disabled = lines.length === 0 || lines.some((line) => !line.available);
  }

  async add() {
    const lines = this.lines;
    if (!lines.length || this.button.disabled) return;
    const message = this.querySelector('[data-bundle-message]');
    if (message) {
      message.textContent = '';
      message.hidden = true;
    }
    this.button.disabled = true;
    this.button.setAttribute('aria-busy', 'true');
    const result = await addItems(
      this,
      lines.map((line) => ({ id: line.id, quantity: 1 }))
    );
    this.button.removeAttribute('aria-busy');
    this.render();
    if (!result.ok && message) {
      message.textContent = result.message;
      message.hidden = false;
    }
  }
}

if (!customElements.get('nv-offers')) customElements.define('nv-offers', NVOffers);
if (!customElements.get('nv-bundle')) customElements.define('nv-bundle', NVBundle);